<template>
  <div
    data-testid="tm-pending-payouts__view"
    class="tm-pending-payouts transaction-matching-consolidated-results transaction-matching-consolidated-results--v2"
  >
    <TMPendingPayoutsToolbar
      v-if="collection"
      :total-amount="totalAmount"
      :total-count="totalCount"
      :active-filter-count="activeFilterCount"
      :active-sort-count="activeSortCount"
      :download-history="filteredDownloadHistory"
      :loading-history="loadingHistory"
      :show-sort-modal="showSortModal"
      :active-sort-tab="activeSortTab"
      :available-sort-columns="availableSortColumns"
      :saved-sort="savedSort"
      :playground-sort="playgroundSort"
      :enable-saved-filter-sort="false"
      :page="page"
      :page-count="totalPages"
      :has-loaded-data="hasLoadedData"
      :loading="loading"
      @back="$emit('back')"
      @refresh="fetchData({ forceRefresh: true })"
      @open-filter="openFilterModal"
      @open-sort="openSortModal"
      @close-sort="closeSortModal"
      @update:active-sort-tab="activeSortTab = $event"
      @toggle-sort-column="toggleSortColumn"
      @set-sort-direction="setSortDirection"
      @reset-sort="resetSort"
      @apply-sort="handleApplySort"
      @page-change="onPageChange"
      @generate-excel="handleGenerateExcel"
      @refresh-download-history="refreshDownloadHistory"
      @download-history-file="handleDownloadFile"
    />

    <div
      v-if="loading && collection && !records.length"
      class="consolidated-results__loading"
      data-testid="tm-pending-payouts__loading"
    >
      <img
        src="/img/loader_new.svg"
        alt="Loading"
        class="consolidated-results__loading-image"
        data-testid="tm-pending-payouts__loading-image"
      />
    </div>

    <template v-else-if="collection && hasLoadedData">
      <TMResultsTableWrapper
        v-if="records.length"
        :table-headers="tableHeaders"
        :rows="displayRows"
        :loading="loading"
        :draggable-headers="true"
        variant="v2"
        :applied-sort="appliedSort"
        :applied-filters="appliedFilters"
        :column-type-map="columnTypeMap"
        :status-column-keys="[]"
        data-testid="tm-pending-payouts__table"
        @drag-start="handleDragStart"
        @drag-over="handleDragOver"
        @drag-leave="handleDragLeave"
        @drop="handleDrop"
        @drag-end="handleDragEnd"
      />

      <div
        v-if="!loading && records.length === 0"
        class="text-center grey--text pa-4"
        data-testid="tm-pending-payouts__empty"
      >
        No pending payout records found for this date.
      </div>
    </template>

    <div
      v-else-if="!collection"
      class="text-center grey--text pa-8"
      data-testid="tm-pending-payouts__no-collection"
    >
      <v-icon large class="mb-3" color="grey lighten-1">mdi-database-off-outline</v-icon>
      <div class="subtitle-1 mb-1">No data available</div>
      <div class="body-2">Please re-run the analysis to generate Pending Payouts data.</div>
    </div>

    <div
      v-if="showFilterModal"
      data-testid="tm-pending-payouts__filter-modal-overlay"
      class="consolidated-results__filter-modal-overlay"
      @click.self="closeFilterModal"
    >
      <div
        data-testid="tm-pending-payouts__filter-modal"
        class="consolidated-results__filter-modal"
      >
        <div class="consolidated-results__filter-modal-header">
          <h3 class="consolidated-results__filter-modal-title">Filter Pending Payouts</h3>
          <button
            type="button"
            data-testid="tm-pending-payouts__filter-modal-close"
            class="consolidated-results__filter-modal-close"
            @click="closeFilterModal"
          >
            <svg class="consolidated-results__filter-modal-close-icon" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
              <path d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div class="consolidated-results__filter-modal-body">
          <TMResultsFilterBuilder
            test-id-prefix="tm-pending-payouts"
            :filter-groups="playgroundFilters.groups"
            :grouping-connector="playgroundFilterGroupingConnector"
            :filter-columns="filterColumns"
            :filter-operators="filterOperators"
            :has-active-filters="(appliedFilters?.groups || []).length > 0"
            @set-grouping-connector="playgroundFilterGroupingConnector = $event"
            @add-group="addGroup"
            @remove-group="removeGroup"
            @reset="resetFilters"
            @apply="applyFilters"
            @update-group-name="updateGroupName"
            @update-group-connector="updateGroupConnector"
            @update-condition="updateCondition"
            @add-condition="addCondition"
            @remove-condition="removeCondition"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TMPendingPayoutsToolbar from './TMPendingPayoutsToolbar.vue';
import TMResultsFilterBuilder from '../shared/TMResultsFilterBuilder.vue';
import TMResultsTableWrapper from '../shared/TMResultsTableWrapper.vue';
import {
  FILTER_OPERATORS,
  createDefaultFilterGroup,
  createDefaultCondition,
  buildFilterPayload,
  buildPipelineFromFiltersAndSort,
  ensureFilterIds,
  hasValidFilterContent,
  formatHeaderText,
} from '../shared/tmResultsDataTableUtils.js';
import {
  buildFilterColumnsFromApi,
  countActiveFilterConditions,
  buildTableHeadersFromApi,
} from './tmReportDrilldownViewUtils.js';
import { TM_DOWNLOAD_SECTIONS } from '../../../../utils/constants.js';

export default {
  name: 'TMPendingPayoutsView',

  components: {
    TMPendingPayoutsToolbar,
    TMResultsFilterBuilder,
    TMResultsTableWrapper,
  },

  props: {
    collection: {
      type: String,
      default: null,
    },
    reportDate: {
      type: String,
      default: null,
    },
    analysisDetails: {
      type: Object,
      default: () => ({}),
    },
  },

  data() {
    return {
      columnHeaders: [],
      loading: false,
      loadingHistory: false,
      records: [],
      totalCount: 0,
      totalAmount: 0,
      page: 1,
      limit: 50,
      totalPages: 0,
      currentHeaderOrder: [],
      draggedHeaderKey: null,
      currentDropTarget: null,
      pollInterval: null,
      hasLoadedData: false,
      showFilterModal: false,
      showSortModal: false,
      playgroundFilters: { groups: [], group_connectors: [] },
      modalFilterSnapshot: null,
      playgroundFilterGroupingConnector: 'OR',
      appliedFilters: { groups: [], group_connectors: [] },
      appliedSort: [],
      savedSort: [],
      playgroundSort: [],
      activeSortTab: 'playground',
      filterOperators: FILTER_OPERATORS,
    };
  },

  computed: {
    ...mapGetters('transactionMatching', ['downloadHistory']),

    filteredDownloadHistory() {
      return (this.downloadHistory || []).filter((file) => {
        if (file?.section) return file.section === TM_DOWNLOAD_SECTIONS.PENDING_PAYOUTS;
        return !Array.isArray(file?.tables) || file.tables.length === 0 || file.tables.includes(TM_DOWNLOAD_SECTIONS.PENDING_PAYOUTS);
      });
    },

    activeFilterCount() {
      return countActiveFilterConditions(this.appliedFilters);
    },

    activeSortCount() {
      return Array.isArray(this.appliedSort) ? this.appliedSort.length : 0;
    },

    effectiveHeaderKeys() {
      if (this.currentHeaderOrder.length > 0) {
        return this.currentHeaderOrder;
      }
      return this.columnHeaders || [];
    },

    tableHeaders() {
      return buildTableHeadersFromApi(this.effectiveHeaderKeys, this.columnTypeMap);
    },

    displayRows() {
      return this.records.map((row, idx) => {
        const mapped = { _id: row._id };
        this.effectiveHeaderKeys.forEach((key) => {
          const value = this.resolveCell(row, key, idx);
          if (value === null || value === undefined || value === 'nan') {
            mapped[key] = '';
          } else {
            mapped[key] = value;
          }
        });
        return mapped;
      });
    },

    filterColumns() {
      return buildFilterColumnsFromApi(this.columnHeaders);
    },

    availableSortColumns() {
      return (this.columnHeaders || []).map((key) => ({
        key,
        label: formatHeaderText(key),
      }));
    },

    columnTypeMap() {
      const map = {};
      (this.columnHeaders || []).forEach((key) => {
        const lower = String(key).toLowerCase();
        if (lower.includes('amount') || lower.includes('count') || lower.includes('qty')) {
          map[key] = 'number';
        } else if (lower.includes('date')) {
          map[key] = 'date';
        } else {
          map[key] = 'string';
        }
      });
      return map;
    },
  },

  mounted() {
    if (this.collection) {
      this.fetchData();
      this.refreshDownloadHistory();
    }
  },

  beforeDestroy() {
    this.stopPolling();
  },

  methods: {
    ...mapActions('transactionMatching', ['fetchPendingPayoutsReport', 'generateResult', 'fetchDownloadHistory']),

    syncHeaderOrderFromApi(headers, { reset = false } = {}) {
      const incoming = (headers || []).filter((key) => key && !String(key).startsWith('_'));
      if (reset || !this.currentHeaderOrder.length) {
        this.currentHeaderOrder = [...incoming];
        return;
      }

      const incomingSet = new Set(incoming);
      const sameColumnSet = this.currentHeaderOrder.length === incoming.length
        && this.currentHeaderOrder.every((key) => incomingSet.has(key));

      if (!sameColumnSet) {
        this.currentHeaderOrder = [...incoming];
        return;
      }

      this.currentHeaderOrder = this.currentHeaderOrder.filter((key) => incomingSet.has(key));
    },

    handleDragStart(headerKey, index, event) {
      this.draggedHeaderKey = headerKey;
      event.dataTransfer.effectAllowed = 'move';
      event.dataTransfer.setData('text/plain', headerKey);
      event.target.classList.add('consolidated-results__header-cell--dragging');
    },

    handleDragOver(headerKey, index, event) {
      if (this.draggedHeaderKey === null) return;

      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';

      const targetCell = event.currentTarget;
      if (headerKey !== this.draggedHeaderKey && this.currentDropTarget !== targetCell) {
        if (this.currentDropTarget) {
          this.currentDropTarget.classList.remove('consolidated-results__header-cell--drop-target');
        }
        targetCell.classList.add('consolidated-results__header-cell--drop-target');
        this.currentDropTarget = targetCell;
      }
    },

    handleDragLeave(event) {
      const relatedTarget = event.relatedTarget;
      if (!event.currentTarget.contains(relatedTarget)) {
        event.currentTarget.classList.remove('consolidated-results__header-cell--drop-target');
        if (this.currentDropTarget === event.currentTarget) {
          this.currentDropTarget = null;
        }
      }
    },

    handleDrop(targetHeaderKey, targetIndex, event) {
      event.preventDefault();

      if (this.draggedHeaderKey === null || this.draggedHeaderKey === targetHeaderKey) {
        this.clearDragState();
        return;
      }

      const sourceIndex = this.currentHeaderOrder.indexOf(this.draggedHeaderKey);
      const destIndex = this.currentHeaderOrder.indexOf(targetHeaderKey);

      if (sourceIndex === -1 || destIndex === -1) {
        this.clearDragState();
        return;
      }

      const newOrder = [...this.currentHeaderOrder];
      const [draggedItem] = newOrder.splice(sourceIndex, 1);
      newOrder.splice(destIndex, 0, draggedItem);
      this.currentHeaderOrder = newOrder;
      this.clearDragState();
    },

    clearDragState() {
      this.draggedHeaderKey = null;
      this.currentDropTarget = null;
      document.querySelectorAll('.consolidated-results__header-cell--dragging').forEach((el) => {
        el.classList.remove('consolidated-results__header-cell--dragging');
      });
      document.querySelectorAll('.consolidated-results__header-cell--drop-target').forEach((el) => {
        el.classList.remove('consolidated-results__header-cell--drop-target');
      });
    },

    handleDragEnd() {
      this.clearDragState();
    },

    resolveCell(row, key, idx) {
      switch (key) {
        case 'sno':
          return String((this.page - 1) * this.limit + idx + 1);
        case 'society':
          return row.mygate_society_id ?? row.society_id;
        case 'name':
          return row.name;
        case 'location':
          return row.location;
        case 'date':
          return row.date || row.internal_report_date;
        case 'reference':
          return row.reference || row.request_id || row.txnid;
        case 'amount':
          return row.amount;
        case 'status':
          return row.status || row.payout_status;
        default:
          return row[key];
      }
    },

    openFilterModal() {
      this.initializeFilterState();
      this.modalFilterSnapshot = {
        playgroundFilters: JSON.parse(JSON.stringify(this.playgroundFilters)),
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
      };
      this.showFilterModal = true;
    },

    initializeFilterState() {
      if ((this.appliedFilters?.groups || []).length > 0) {
        const hydrated = ensureFilterIds(this.appliedFilters);
        this.playgroundFilters = hydrated || this.createEmptyFilterGroup();
        this.playgroundFilterGroupingConnector = this.appliedFilters.group_connectors?.[0] || 'OR';
        return;
      }

      if (!this.playgroundFilters?.groups?.length) {
        this.playgroundFilters = this.createEmptyFilterGroup();
        return;
      }

      const ensured = ensureFilterIds(this.playgroundFilters);
      if (ensured) {
        this.playgroundFilters = ensured;
      }
    },

    closeFilterModal(restore = true) {
      this.showFilterModal = false;
      if (restore && this.modalFilterSnapshot) {
        this.playgroundFilters = JSON.parse(JSON.stringify(this.modalFilterSnapshot.playgroundFilters));
        this.playgroundFilterGroupingConnector = this.modalFilterSnapshot.playgroundFilterGroupingConnector;
      }
    },

    createEmptyFilterGroup() {
      return {
        groups: [createDefaultFilterGroup()],
        group_connectors: [],
      };
    },

    addGroup() {
      this.playgroundFilters.groups.push(createDefaultFilterGroup());
    },

    removeGroup(groupId) {
      if (this.playgroundFilters.groups.length === 1) {
        this.resetFilters();
        return;
      }
      this.playgroundFilters.groups = this.playgroundFilters.groups.filter((g) => g.id !== groupId);
    },

    resetFilters() {
      this.playgroundFilters = this.createEmptyFilterGroup();
      this.playgroundFilterGroupingConnector = 'OR';
    },

    addCondition(groupId) {
      const group = this.playgroundFilters.groups.find((g) => g.id === groupId);
      if (group) {
        group.conditions.push(createDefaultCondition());
      }
    },

    removeCondition(groupId, conditionId) {
      const group = this.playgroundFilters.groups.find((g) => g.id === groupId);
      if (!group) return;
      const remaining = group.conditions.filter((c) => c.id !== conditionId);
      group.conditions = remaining.length > 0 ? remaining : [createDefaultCondition()];
    },

    updateCondition(groupId, conditionId, field, value) {
      const group = this.playgroundFilters.groups.find((g) => g.id === groupId);
      if (!group) return;
      const condition = group.conditions.find((c) => c.id === conditionId);
      if (condition) {
        this.$set(condition, field, value);
      }
    },

    updateGroupConnector(groupId, connector) {
      const group = this.playgroundFilters.groups.find((g) => g.id === groupId);
      if (group) {
        this.$set(group, 'connector', connector);
      }
    },

    updateGroupName(groupId, name) {
      const group = this.playgroundFilters.groups.find((g) => g.id === groupId);
      if (group) {
        this.$set(group, 'name', name);
      }
    },

    applyFilters() {
      const payload = buildFilterPayload(
        this.playgroundFilters.groups || [],
        this.playgroundFilterGroupingConnector,
        this.columnTypeMap
      );
      this.appliedFilters = payload;
      this.modalFilterSnapshot = {
        playgroundFilters: JSON.parse(JSON.stringify(this.playgroundFilters)),
        playgroundFilterGroupingConnector: this.playgroundFilterGroupingConnector,
      };
      this.closeFilterModal(false);
      this.page = 1;
      this.fetchData();
    },

    openSortModal() {
      if (this.playgroundSort.length === 0 && this.appliedSort.length > 0) {
        this.playgroundSort = JSON.parse(JSON.stringify(this.appliedSort));
      }
      this.activeSortTab = 'playground';
      this.showSortModal = true;
    },

    closeSortModal() {
      this.showSortModal = false;
    },

    getCurrentSortItems() {
      return this.activeSortTab === 'saved' ? this.savedSort : this.playgroundSort;
    },

    toggleSortColumn(columnKey) {
      const currentSort = [...this.getCurrentSortItems()];
      const index = currentSort.findIndex((item) => item.key === columnKey);
      if (index >= 0) {
        currentSort.splice(index, 1);
      } else {
        currentSort.push({ key: columnKey, direction: 'asc' });
      }
      this.playgroundSort = currentSort;
    },

    setSortDirection(columnKey, direction) {
      this.playgroundSort = this.getCurrentSortItems().map((item) => (
        item.key === columnKey ? { ...item, direction } : item
      ));
    },

    resetSort() {
      this.playgroundSort = [];
    },

    handleApplySort() {
      this.appliedSort = JSON.parse(JSON.stringify(this.playgroundSort));
      this.closeSortModal();
      this.page = 1;
      this.fetchData();
    },

    buildRequestSort() {
      return this.appliedSort.map((item) => ({
        field: item.key,
        direction: item.direction,
      }));
    },

    buildDataFilters() {
      const pipeline = [];

      if (this.reportDate) {
        let normalizedDate = this.reportDate;
        if (/^\d{2}-\d{2}-\d{4}$/.test(this.reportDate)) {
          const [d, m, y] = this.reportDate.split('-');
          normalizedDate = `${y}-${m}-${d}`;
        }
        pipeline.push({
          $match: {
            $or: [
              { Record_report_date: { $eq: normalizedDate } },
              { report_date: { $eq: normalizedDate } },
            ],
          },
        });
      }

      if (hasValidFilterContent(this.appliedFilters)) {
        const filterPipelineJson = buildPipelineFromFiltersAndSort(
          this.appliedFilters,
          null,
          this.appliedFilters.group_connectors?.[0] || 'OR',
          this.columnTypeMap
        );
        const filterStages = JSON.parse(filterPipelineJson);
        pipeline.push(...filterStages);
      }

      const sort = this.buildRequestSort();
      if (sort.length > 0) {
        const sortObject = {};
        sort.forEach((item) => {
          sortObject[item.field] = item.direction === 'desc' ? -1 : 1;
        });
        pipeline.push({ $sort: sortObject });
      }

      return pipeline.length > 0 ? JSON.stringify(pipeline) : null;
    },

    async handleGenerateExcel() {
      const botId = this.analysisDetails?.bpid;
      if (!botId) return;
      try {
        await this.generateResult({
          item: TM_DOWNLOAD_SECTIONS.PENDING_PAYOUTS,
          botId,
          collection: this.collection,
          tables: [TM_DOWNLOAD_SECTIONS.PENDING_PAYOUTS],
          dataFilters: this.buildDataFilters(),
        });
      } catch {
        // Store already surfaces the error via snackbar
      }
    },

    async refreshDownloadHistory() {
      const botId = this.analysisDetails?.bpid;
      if (!botId) return;
      this.loadingHistory = true;
      try {
        await this.fetchDownloadHistory(botId);
      } finally {
        this.loadingHistory = false;
      }
    },

    handleDownloadFile(file) {
      if (file && file.upload) {
        this.$nova.downloadFile(file.upload);
      } else {
        this.$store.commit('sbError', 'File not available for download', { root: true });
      }
    },

    async fetchData({ forceRefresh = false } = {}) {
      this.loading = true;
      if (forceRefresh) {
        this.stopPolling();
        this.records = [];
        this.columnHeaders = [];
        this.currentHeaderOrder = [];
        this.hasLoadedData = false;
      }
      try {
        const sort = this.buildRequestSort();
        const payload = {
          collection: this.collection,
          reportDate: this.reportDate,
          page: this.page,
          limit: this.limit,
          client: this.analysisDetails.client,
          tmaid: this.analysisDetails._id,
          bpid: this.analysisDetails.bpid,
          forceRefresh,
          filters: this.appliedFilters,
        };
        if (sort.length > 0) {
          payload.sort = sort;
        }

        const result = await this.fetchPendingPayoutsReport(payload);

        if (result.reportStatus === 'ready') {
          const incomingHeaders = result.headers || [];
          if (incomingHeaders.length > 0) {
            this.columnHeaders = incomingHeaders;
            this.syncHeaderOrderFromApi(this.columnHeaders, { reset: forceRefresh });
          } else if (forceRefresh) {
            this.columnHeaders = [];
            this.currentHeaderOrder = [];
          }
          this.records = result.records || [];
          this.totalCount = result.totalCount || 0;
          this.totalAmount = result.totalAmount != null
            ? result.totalAmount
            : this.records.reduce((sum, r) => sum + (Number(r.amount) || 0), 0);
          this.totalPages = result.totalPages || 0;
          this.hasLoadedData = true;
          this.stopPolling();
          this.loading = false;
        } else if (result.reportStatus === 'processing') {
          if (forceRefresh) {
            this.records = [];
            this.columnHeaders = [];
          }
          this.startPolling();
        } else {
          this.loading = false;
        }
      } catch (err) {
        this.loading = false;
        this.stopPolling();
        this.$store.commit('sbError', err.message || 'Failed to load pending payouts', { root: true });
      }
    },

    onPageChange(newPage) {
      const targetPage = Number(newPage) || 1;
      if (targetPage === this.page) return;
      this.page = targetPage;
      this.fetchData();
    },

    startPolling() {
      this.stopPolling();
      this.pollInterval = setInterval(() => this.fetchData(), 10000);
    },

    stopPolling() {
      if (this.pollInterval) {
        clearInterval(this.pollInterval);
        this.pollInterval = null;
      }
    },
  },
};
</script>

<style lang="scss">
@import '../shared/TMResultsDataTable.scss';
</style>

<style lang="scss" scoped>
@import './TMPendingPayoutsView.scss';
</style>
